import { InsightsLanding } from "@/features/insights/components/InsightsLanding";
import { InsightsProcessing } from "@/features/insights/components/InsightsProcessing";
import { InsightsResults } from "@/features/insights/components/InsightsResults";
import { useApi } from "@/hooks/use-api";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { InsightsChatRequest } from "@/types/insights";
import { FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to generate insights. Please try again.";
};

const isSessionNotFoundError = (
  error: unknown
): error is Error & { status: number } => {
  return (
    error instanceof Error &&
    "status" in error &&
    (error as { status?: number }).status === 404 &&
    error.message === "Insight session not found"
  );
};

export const Insights = () => {
  const dispatch = useAppDispatch();
  const assessments = useAppState((state) => state.assessments || []);
  const courses = useAppState((state) => state.courses || []);
  const documents = useAppState((state) => state.documents || []);
  const insights = useAppState((state) => state.insights);
  const { askInsights, updateDocument, fetchDocuments } = useApi();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flaggingByDocumentId, setFlaggingByDocumentId] = useState<
    Record<string, boolean>
  >({});
  const {
    query,
    selectedContextValue,
    phase,
    result,
    error,
    lastRequest,
    sessionId,
  } = insights;

  const setInsightsState = (payload: Partial<typeof insights>) => {
    dispatch({ type: "SET_INSIGHTS_STATE", payload });
  };

  const contextOptions = useMemo(() => {
    const gradedAssessments = assessments.filter(
      (assessment) => assessment.gradedCount
    );

    return courses
      .map((course) => {
        const courseAssessments = gradedAssessments
          .filter((assessment) => assessment.courseId === course.id)
          .map((assessment) => ({
            value: `assessment:${assessment.id}`,
            label: assessment.title,
          }));

        if (courseAssessments.length === 0) {
          return null;
        }

        return {
          label: course.title,
          options: [
            { value: `course:${course.id}`, label: "All assessments" },
            ...courseAssessments,
          ],
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [assessments, courses]);

  const documentsById = useMemo(
    () => new Map(documents.map((doc) => [doc.id, doc])),
    [documents]
  );
  const trimmedQuery = query.trim();
  const canSubmit = trimmedQuery.length > 0 && !isSubmitting;

  const buildContext = (selectedValue?: string | null) => {
    if (!selectedValue) {
      return null;
    }

    if (selectedValue.startsWith("course:")) {
      return { courseId: selectedValue.replace("course:", "") };
    }

    if (selectedValue.startsWith("assessment:")) {
      return { assessmentId: selectedValue.replace("assessment:", "") };
    }

    return null;
  };

  const runSearch = async (request: InsightsChatRequest) => {
    setInsightsState({
      lastRequest: request,
      error: null,
      result: null,
      phase: "loading",
    });
    setIsSubmitting(true);

    try {
      const response = await askInsights(request);
      setInsightsState({
        query: "",
        result: response,
        phase: "result",
        sessionId: response.sessionId,
      });
    } catch (err) {
      if (request.sessionId && isSessionNotFoundError(err)) {
        setInsightsState({ sessionId: null });
        await runSearch({ ...request, sessionId: null });
        return;
      }

      setInsightsState({
        error: getErrorMessage(err),
        phase: "result",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }

    await runSearch({
      query: trimmedQuery,
      sessionId,
      context: buildContext(selectedContextValue),
    });
  };

  const handleFlagDocument = async (documentId: string) => {
    setFlaggingByDocumentId((prev) => ({ ...prev, [documentId]: true }));
    try {
      const currentDocument = documentsById.get(documentId);
      const nextStatus =
        currentDocument?.status === "flagged"
          ? currentDocument.grade
            ? "done"
            : "ready"
          : "flagged";
      await updateDocument(documentId, { status: nextStatus });
      await fetchDocuments();
    } catch {
      toast.error("Failed to update submission flag");
    } finally {
      setFlaggingByDocumentId((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleFollowUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!trimmedQuery || isSubmitting) {
      return;
    }

    await runSearch({
      query: trimmedQuery,
      sessionId,
      context: lastRequest?.context || buildContext(selectedContextValue),
    });
  };

  const handleNewChat = () => {
    setInsightsState({
      query: "",
      result: null,
      error: null,
      phase: "idle",
      lastRequest: null,
      sessionId: null,
      selectedContextValue: undefined,
    });
    setFlaggingByDocumentId({});
  };

  const activePrompt = lastRequest?.query || trimmedQuery;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {phase === "idle" ? (
        <InsightsLanding
          query={query}
          selectedContextValue={selectedContextValue}
          contextOptions={contextOptions}
          canSubmit={canSubmit}
          onQueryChange={(value) => setInsightsState({ query: value })}
          onContextChange={(value) =>
            setInsightsState({ selectedContextValue: value })
          }
          onGenerate={handleGenerate}
        />
      ) : (
        <>
          {phase === "loading" && <InsightsProcessing activePrompt={activePrompt} />}
          {phase === "result" && (
            <InsightsResults
              activePrompt={activePrompt}
              error={error}
              result={result}
              documentsById={documentsById}
              flaggingByDocumentId={flaggingByDocumentId}
              followUpQuery={query}
              canSubmitFollowUp={trimmedQuery.length > 0 && !isSubmitting}
              onFlagDocument={handleFlagDocument}
              onFollowUpQueryChange={(value) => setInsightsState({ query: value })}
              onFollowUp={handleFollowUp}
              onNewChat={handleNewChat}
            />
          )}
        </>
      )}
    </div>
  );
};
